import { html } from "lit";
import { customElement, state } from 'lit/decorators.js';
import store from "../../../store/store";
import { connect } from 'pwa-helpers';
import { StepId } from "../shop-checkout.interface";
import { CheckoutStepsBase } from "../checkout-steps-base";
import "./payment-billing-tunnel-checkout-step";
import { StoreState } from "../../../store/store.interface";
import { Product, ShopCart } from "../../cart/shop-cart/interfaces/shop-cart.interface";
import { getCart, getProductCountryCode } from "../../cart/shop-cart/store/shop-cart.selectors";
import { getBillingAddress, getClientTokenResponse, getDigitalHandlingFeesAmount, getPaymentDetails, getTaxInfo, getTotalPrice, getTotalTax, updateIntlTotalTax } from "../shop-checkout";
import { getPromoCode } from "../../../features/promo-code/promo-code.selectors";
import emit from "../../../common/events";
import { addAnalyticsCheckoutFlowMetadata, getProductEndAccessDate } from "../../../common/utils/cart-util";
import { PaymentMethodType } from "../../payment-methods/payment-method";
import { CountryCode } from "../../country-selector/country-state.interface";
import { getSharpenTunnelFlowProperties } from '../../../common/utils/analytics-util';
import { appendDurationForTunnelPurchaseFlow, getTunnelProductTitleVerbose } from "../../../common/utils/product-util";
import { productTitle } from "../../../common/utils/util";
import { isAleksSharpenCompanionBundle, fetchAleksProductTitleVerbose } from "../../../common/utils/aleks-sharpen-bundle-util";

/**
 * An element to load tunnel checkout steps and complete the checkout process.
 */
@customElement('mhecomm-tunnel-checkout-steps')
export class TunnelCheckoutSteps extends connect(store)(CheckoutStepsBase) {

  @state()
  private setCheckoutStep = true;

  private analyticsTriggeredEvents = {
    checkoutPageEmitted: false,
    reviewPageEmitted: false
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mhecomm-checkout-step-edited', this.resetAnalyticsControl);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('mhecomm-checkout-step-edited', this.resetAnalyticsControl);
  }

  stateChanged(state: StoreState) {
    this.updateCheckoutStepsState(state);
    this.handleApiFailures(state);
    const cart = getCart(store.getState() as StoreState);
    if(cart && this.setCheckoutStep) {
      this.setCheckoutSteps();
      this.setCheckoutStep = false;
      this.updateIntlTaxAmount(cart);
    }
  }
  
  override setStepOrderStepIdMap() {
    this.stepOrderStepIdMap['1'] = StepId.PaymentBillingAddressStep;
    this.isLoading = false;
  }

  override setCartCheckoutContext(cart: ShopCart) {
    this.shippable = cart?.shippable || false;
  }

  resetAnalyticsControl() {
    this.analyticsTriggeredEvents.reviewPageEmitted = false;
  }

  override renderCheckoutSteps() {
    if (this.isComplete && !this.analyticsTriggeredEvents.reviewPageEmitted) {
      this.reviewOrderLoadedAnalytics();
    }

    if (this.activeStepId === StepId.PaymentBillingAddressStep 
      && !this.isComplete
      && !this.analyticsTriggeredEvents.checkoutPageEmitted) {
      this.pageLoadAnalytics();
    }

    return html`
      <mhecomm-payment-billing-tunnel-checkout-step 
        ?forceComplete="${this.forceComplete}"
        id=${StepId.PaymentBillingAddressStep}
        stepName="Payment and Billing"
        nextStepButtonName="Review Order">
      </mhecomm-payment-billing-tunnel-checkout-step>
    `;
  }

  /**
   * When all steps are completed update checkout steps state.
   */
  protected completeSteps() {
    super.completeSteps();
  }

  private pageLoadAnalytics() {
    const cart = getCart(store.getState() as StoreState);
    if (!cart?.products[0]) {
      return;
    }
    const product: Product = cart!.products[0];
    emit({
      element: this,
      name: 'load-tunnel-purchase-checkout',
      detail: addAnalyticsCheckoutFlowMetadata({
        price: `${product.priceInfo.customPrice} ${product.priceInfo.currencyCode}`,
        productTitle: this.getAnalyticsProductTitle(product),
        productImage: product.productMetadata.imageUrl,
        countryCode: product.priceInfo.countryCode,
        licenseEndDate: getProductEndAccessDate(product.productIdentifier.term!),
        ...getSharpenTunnelFlowProperties({
          isSharpenCompanion: product.productIdentifier.isSharpenCompanion,
          isSharpenAdvantage: product.productIdentifier.isSharpenAdvantage,
          isConnectSharpenBundle: product.productIdentifier.isConnectSharpenBundle,
          itemType: product.productMetadata.itemType,
          offerType: product.productMetadata.offerType,
          formatCode: product.productMetadata.formatCode,
          flow: product.productMetadata.flow
        })
      })
    })
    this.analyticsTriggeredEvents.checkoutPageEmitted = true;
  }

  private reviewOrderLoadedAnalytics() {
    const cart = getCart(store.getState() as StoreState);
    const paymentDetails = getPaymentDetails(store.getState() as StoreState);
    if (!cart?.products[0]) {
      return;
    }
    const product: Product = cart!.products[0];
    const clientTokenResponse = getClientTokenResponse(store.getState() as StoreState)!;

    emit({
      element: this,
      name: 'load-tunnel-review-order',
      detail: addAnalyticsCheckoutFlowMetadata({
        price: `${product.priceInfo.customPrice} ${product.priceInfo.currencyCode}`,
        productTitle: product.productMetadata.title,
        productImage: product.productMetadata.imageUrl,
        countryCode: product.priceInfo.countryCode,
        licenseEndDate: getProductEndAccessDate(product.productIdentifier.term!),
        taxValue: getTotalTax(store.getState() as StoreState),
        paymentType: PaymentMethodType.getPaymentMethodType(paymentDetails!.paymentType!).displayName,
        serviceFee: getDigitalHandlingFeesAmount(store.getState() as StoreState),
        totalPrice: getTotalPrice(store.getState() as StoreState),
        subTotal: cart?.totalAmount + cart?.discount,
        promoCode: getPromoCode(store.getState() as StoreState),
        threeDS: clientTokenResponse?.threeDSecureEnabled,
        unit: getBillingAddress(store.getState() as StoreState)?.address2,
        taxDetails: getTaxInfo(store.getState() as StoreState),
      })
    })

    this.analyticsTriggeredEvents.reviewPageEmitted = true
  }

  private updateIntlTaxAmount(cart: ShopCart) {
    const countryCode = getProductCountryCode(store.getState() as StoreState);
    const isCountryNonUSorCA = countryCode !== CountryCode.US && countryCode !== CountryCode.CA;
    if(isCountryNonUSorCA && cart.intlTaxAmount) {
      const taxInfo = { amount: cart.intlTaxAmount};
      store.dispatch(updateIntlTotalTax(taxInfo));
    }
  }

  private getAnalyticsProductTitle(product: Product) {
    return isAleksSharpenCompanionBundle(product.productMetadata?.offerType, product.productIdentifier?.isSharpenCompanion)
      ? fetchAleksProductTitleVerbose(product.productMetadata?.metaOfferContentsMetadata)
      : appendDurationForTunnelPurchaseFlow({
          duration: product.productMetadata.duration,
          durationPeriod: product.productMetadata.durationPeriod,
          title: getTunnelProductTitleVerbose(productTitle(product), product.productIdentifier),
          term: product.productIdentifier.term,
        });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tunnel-checkout-steps': TunnelCheckoutSteps;
  }
}
